import React, { useContext, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import SupportAccordion from '../../components/support-accordion/support-accordion';
import { useRecallsContent } from './hook/use-recalls-content';
import './recalls.scss';
import { PrimaryButton } from '../../../components/common';
import { SearchBar, VehicleSelectorFds } from '../../../components/sections';
import ServerContext from '../../../contexts/serverContext';
import SupportFdsWrapper from '../../components/support-fds-wrapper/support-fds-wrapper';
import { PageNotFoundView } from '../../../views/page-not-found-view/page-not-found-view';
import { InfoIconToolTip } from '../../../components/common/info-icon-tooltip/info-icon-tooltip';
import siteMapService from '../../services/site-map-service/site-map-service';
import { useAnalytics } from '../../../hooks/use-analytics';
import DisclosuresLinkSyndicationAware from '../../../components/sections/disclosures-link/disclosures-link-syndication-aware';
import { RECALLS } from '../../support-constants';
import useSeoConfig from '../../../hooks/use-seo-config';
import ImportantInfoAccordion from '../../../components/sections/important-info-acccordion/important-info-accordion';
import { VideoContent } from '../../../components/sections/onboarding-how-to-videos/htv-video-component/htv-video-component';
import '../../../views/onboarding-htv-video-page/htv-video-detail-view.scss';
import { useVideoContent } from './hook/use-video-content';
import BreadcrumbsAndViewBanner from '../../components/breadcrumbs-and-view-banner/breadcrumbs-and-view-banner';
import { useNotificationBannerContent } from '../../../components/sections/notification-banner/hooks/use-notification-banner';
import NotificationBanner from '../../../components/sections/notification-banner/notification-banner';
import WebFlashMessage from '../../../components/common/web-flash-message/web-flash-message';
import { ActivityIndicator } from '../../../components/common/activity-indicator/activity-indicator';
import VehicleSelectorTabs from '../../../components/common/vehicle-selector-tabs/vehicle-selector-tabs';
import { useParams } from 'react-router-dom';
import { useVehicleSelectorTabsContent } from '../../../components/common/vehicle-selector-tabs/hooks/use-vehicle-selector-tabs-content';
import { isIMGTakataRoute } from '../recalls-details/recalls-util';
import useFooterDisplay from '../../../hooks/use-footer-display';
import {
    displayEUFooter,
    hideEUFooter,
} from '../../../components/utils/syndicate-util/syndicate-util';
import { EU_COUNTRIES } from '../../../constants';
import { format } from 'date-fns';
import { useHelpCardsContent } from '../../../views/page-not-found-view/hooks/use-help-cards-content';
import { useSearchContent } from '../../../components/sections/search-bar/hooks/use-search-content';

interface BannerData {
    bannerMessage: string;
    dismissAriaLabel: string;
}

interface RecallsParams {
    vin: string;
}

const RecallsPage = () => {
    const { vin } = useParams<RecallsParams>();
    const serverContext = useContext(ServerContext);
    const isImgTakataRoute = isIMGTakataRoute();
    const vehicleSelectorTabsLabels = useVehicleSelectorTabsContent();
    const [loading, setLoading] = useState(true);
    const getCurrentDate = () => format(new Date(), 'MM/dd/yyyy');
    const globalNotificationBannerContent = useNotificationBannerContent(
        '',
        'global-notification-banner'
    );
    const helpCardsContent = useHelpCardsContent();
    const searchContent = useSearchContent();
    const videoContent = useVideoContent(
        RECALLS.RECALLS_CATEGORY,
        RECALLS.VIDEO_CONTENT
    );
    const [bannerData, setBannerData] = useState<BannerData>();
    const baseUrl = siteMapService.getBaseUrl(
        serverContext.currentLanguageRegionCode,
        serverContext.brand
    );
    serverContext.canonical = baseUrl.domain
        .concat(baseUrl.root)
        .concat(
            siteMapService.getPathFromRouterConfigs(
                serverContext.currentLanguageRegionCode,
                serverContext.brand,
                'RecallsPage',
                []
            ) || ''
        );
    const [fireEvents] = useAnalytics();
    let recallsContentPath: string[] = [];
    let seoName = 'recalls-landing';
    let recallsPath = 'recalls';
    let recallsPageLoadEvent = 'owner-recalls';
    let recallsCtaClickEvent = 'recalls-cta';
    let recallsFaqClickEvent = 'recalls-faq';
    if (isImgTakataRoute) {
        recallsContentPath = ['takata-recalls', 'takata-recalls'];
        seoName = 'takata-recalls-landing';
        recallsPath = 'takata-recalls';
        recallsPageLoadEvent = 'owner-takata-recalls';
        recallsCtaClickEvent = 'takata-recalls-cta';
        recallsFaqClickEvent = 'takata-recalls-faq';
    }
    useSeoConfig(seoName, recallsPath);
    let recallsContent = useRecallsContent(...recallsContentPath);
    let timeOut: any = null;
    const startTimeout = () => {
        timeOut = setTimeout(() => {
            if (loading) {
                recallsContent = null;
                setLoading(false);
            }
        }, 2000);
    };
    useEffect(() => {
        window.scrollTo(0, 0);
        if (vin) localStorage.setItem('USER_VIN', vin);
        fireEvents(
            [recallsPageLoadEvent],
            undefined,
            {
                recalls: recallsPath,
            },
            false
        );
        if (window.sessionStorage.getItem('addedVehicleBannerData')) {
            setBannerData(
                JSON.parse(
                    window.sessionStorage.getItem('addedVehicleBannerData')!
                )
            );
            window.sessionStorage.removeItem('addedVehicleBannerData');
        }
        startTimeout();

        return () => {
            clearTimeout(timeOut);
        };
    }, []);
    const vinTooltip = (
        <InfoIconToolTip
            osbServiceTooltip={true}
            tooltipInfoIconClass={'dark'}
            tooltipHeader={recallsContent?.vinTooltipHeader}
            tooltipContent={recallsContent?.vinTooltipContent || ''}
            customShowTooltipAriaLabel={recallsContent?.vinTooltipAriaLabel}
        />
    );

    useEffect(() => {
        if (
            serverContext.brand === 'ford' &&
            EU_COUNTRIES.includes(serverContext.currentRegionCode)
        ) {
            hideEUFooter();
        }
    }, []);

    useEffect(() => {
        if (
            serverContext.brand === 'ford' &&
            EU_COUNTRIES.includes(serverContext.currentRegionCode) &&
            recallsContent &&
            recallsContent?.scheduleServiceTitle
        ) {
            displayEUFooter();
        }
    }, [recallsContent, recallsContent?.scheduleServiceTitle]);
    useFooterDisplay(
        serverContext.brand === 'ford'
            ? [recallsContent]
            : [recallsContent, recallsContent?.scheduleServiceTitle]
    );

    return (
        <>
            {recallsContent?.hideContent === false &&
            recallsContent.recallsDescription !== 'loading' ? (
                <SupportFdsWrapper>
                    <div>
                        {bannerData && (
                            <div
                                className="add-vehicle-banner"
                                role="alert"
                                aria-live="polite"
                            >
                                <div>
                                    <img
                                        src="icons/success-checkmark.svg"
                                        alt=""
                                    />
                                    <span className="fds-content">
                                        {bannerData.bannerMessage}
                                    </span>
                                </div>
                                <button
                                    onClick={event => {
                                        event.preventDefault();
                                        setBannerData(undefined);
                                    }}
                                    aria-label={bannerData.dismissAriaLabel}
                                >
                                    <img
                                        className="close-icon"
                                        src="icons/close.svg"
                                        alt=""
                                    />
                                </button>
                            </div>
                        )}
                    </div>

                    <WebFlashMessage
                        experience={'recalls'}
                        fragmentName={'experience-notification-banner'}
                    />

                    {globalNotificationBannerContent &&
                        globalNotificationBannerContent.bannerText && (
                            <NotificationBanner
                                hide={globalNotificationBannerContent?.hide}
                                type={globalNotificationBannerContent?.type}
                                bannerDismissible={
                                    globalNotificationBannerContent?.bannerDismissible
                                }
                                dismissAriaLabel={
                                    globalNotificationBannerContent?.dismissAriaLabel
                                }
                                bannerText={
                                    globalNotificationBannerContent?.bannerText
                                }
                                showChevron={
                                    globalNotificationBannerContent?.showChevron
                                }
                                contentId="original-recalls-global-notif-banner"
                            />
                        )}
                    <BreadcrumbsAndViewBanner
                        currentPage={recallsContent?.title}
                        hashId={'support'}
                    />

                    {recallsContent && (
                        <>
                            <div className="recalls-content">
                                <div className="width-90-mobile">
                                    <div className="recalls-header-container">
                                        <h1>{recallsContent.title}</h1>
                                        {recallsContent.recallsLastUpdatedText && (
                                            <div className="recalls-last-updated">
                                                {recallsContent.recallsLastUpdatedText +
                                                    ' ' +
                                                    getCurrentDate()}
                                            </div>
                                        )}
                                    </div>
                                    {recallsContent.recallsBannerSubtitle && (
                                        <h2 className="info-title">
                                            {
                                                recallsContent.recallsBannerSubtitle
                                            }
                                        </h2>
                                    )}

                                    {recallsContent.recallsBannerDescription && (
                                        <p className="recalls-content-text">
                                            {
                                                recallsContent.recallsBannerDescription
                                            }
                                        </p>
                                    )}

                                    <h2 className="info-title">
                                        {recallsContent.recallsSubtitle}
                                    </h2>
                                    <p className="recalls-content-text">
                                        {recallsContent.recallsDescription}
                                    </p>
                                </div>
                                {vehicleSelectorTabsLabels?.vehicleSelectorTabsTitle ? (
                                    <VehicleSelectorTabs
                                        redirectData={{
                                            path:
                                                siteMapService.getPathFromRouterConfigs(
                                                    serverContext.currentLanguageRegionCode,
                                                    serverContext.brand,
                                                    'RecallsDetailsPage',
                                                    []
                                                ) || '',
                                            id: 'recalls',
                                        }}
                                        content={vehicleSelectorTabsLabels}
                                    />
                                ) : (
                                    <VehicleSelectorFds
                                        tooltip={vinTooltip}
                                        redirectData={{
                                            path:
                                                siteMapService.getPathFromRouterConfigs(
                                                    serverContext.currentLanguageRegionCode,
                                                    serverContext.brand,
                                                    'RecallsDetailsPage',
                                                    [],
                                                    isImgTakataRoute
                                                ) || '',
                                            id: 'recalls',
                                        }}
                                        labels={{
                                            vinHeader:
                                                recallsContent.vehicleSelectorVinSubtitle,
                                            vinPlaceholder:
                                                recallsContent.vehicleSelectorVinInput,
                                            yearDropdown: '',
                                            modelDropdown: '',
                                            submit:
                                                recallsContent.vehicleSelectorSubmitButtonLabel,
                                            disclaimer:
                                                recallsContent.vehicleSelectorDisclaimerLabel,
                                            header:
                                                recallsContent.vehicleSelectorVinSubtitle,
                                            dividerText:
                                                recallsContent.vehicleSelectorDividerText,
                                        }}
                                    />
                                )}
                                {videoContent && (
                                    <div className="video-content">
                                        <div className="video-page-title">
                                            <h2>{videoContent?.videoTitle}</h2>
                                        </div>
                                        <div>
                                            <VideoContent
                                                videoId={videoContent.videoId}
                                                account={videoContent.accountId}
                                                description={
                                                    videoContent.videoDescription
                                                }
                                                videoTitle={
                                                    videoContent?.videoTitle
                                                }
                                            />
                                        </div>
                                    </div>
                                )}
                                {recallsContent.scheduleServiceTitle && (
                                    <div className="width-90-mobile">
                                        <h2 className="info-title">
                                            {
                                                recallsContent.scheduleServiceTitle
                                            }
                                        </h2>
                                        <div
                                            className="recalls-content-text"
                                            dangerouslySetInnerHTML={{
                                                __html: DOMPurify.sanitize(
                                                    recallsContent.scheduleServiceDescription
                                                ),
                                            }}
                                        />

                                        {recallsContent.findADealerButtonText && (
                                            <div className="content-2-button">
                                                <PrimaryButton
                                                    color="dark"
                                                    fill="fill"
                                                    chevron={true}
                                                    href={
                                                        recallsContent?.findADealerButtonLink
                                                    }
                                                    onClick={() => {
                                                        fireEvents(
                                                            recallsCtaClickEvent,
                                                            undefined,
                                                            {
                                                                ctaType:
                                                                    'find a dealer',
                                                            },
                                                            false
                                                        );
                                                    }}
                                                >
                                                    <span>
                                                        {
                                                            recallsContent.findADealerButtonText
                                                        }
                                                    </span>
                                                </PrimaryButton>
                                            </div>
                                        )}
                                        {recallsContent.landingPageDisclaimer && (
                                            <div
                                                className={
                                                    'recalls-disclaimer-text'
                                                }
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(
                                                        recallsContent.landingPageDisclaimer
                                                    ),
                                                }}
                                                data-testid={
                                                    'recalls-disclaimer-text'
                                                }
                                            />
                                        )}
                                        {recallsContent.accordionTitle && (
                                            <SupportAccordion
                                                contentServiceDirectory="recalls"
                                                contentServiceName="recallsAccordion"
                                                contentServiceComponentName="accordion"
                                                header={
                                                    recallsContent.accordionTitle
                                                }
                                                description={
                                                    recallsContent.accordionDescription
                                                }
                                                eventName={recallsFaqClickEvent}
                                            />
                                        )}
                                        {recallsContent.relatedTopicsContent && (
                                            <>
                                                <h2 className="info-title">
                                                    {
                                                        recallsContent.relatedTopicsTitle
                                                    }
                                                </h2>
                                                <div
                                                    className="recalls-content-3"
                                                    dangerouslySetInnerHTML={{
                                                        __html: DOMPurify.sanitize(
                                                            recallsContent.relatedTopicsContent
                                                        ),
                                                    }}
                                                />
                                            </>
                                        )}
                                    </div>
                                )}
                                {serverContext.brand === 'lincoln' &&
                                    serverContext.currentLanguageRegionCode ===
                                        'en-us' &&
                                    searchContent &&
                                    helpCardsContent && (
                                        <SearchBar
                                            searchContent={searchContent}
                                            helpCardsContent={helpCardsContent}
                                        />
                                    )}
                                <DisclosuresLinkSyndicationAware />
                                <ImportantInfoAccordion
                                    expanded={true}
                                    aemFolder="recalls"
                                    accordionContent={
                                        RECALLS.IMPORTANT_INFO_ACCORDION
                                    }
                                />
                                <ImportantInfoAccordion
                                    aemFolder="recalls"
                                    accordionContent={
                                        RECALLS.DISCLAIMERS_ACCORDION_CONTENT
                                    }
                                />
                            </div>
                        </>
                    )}
                </SupportFdsWrapper>
            ) : (
                <>
                    {recallsContent &&
                    recallsContent?.recallsDescription === 'loading' ? (
                        <ActivityIndicator />
                    ) : (
                        <PageNotFoundView />
                    )}
                </>
            )}
        </>
    );
};

export default RecallsPage;
